import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/providers/auth-provider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useTeamLeaderAssignments } from '@/hooks/use-team-leader';
import { calculateSessionHours } from '@/lib/time-utils';
import {
  Users, Clock, Activity, Eye, Loader2, Monitor, Globe,
  Camera, BarChart3, Search, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  getTodayQatar,
  getTodayQatarRange,
  getQatarDayRange,
  getQatarWeekRange,
  getQatarRangeFromDates,
  startOfDayQatar,
  endOfDayQatar,
  utcToQatarDateStr,
} from '@/utils/timezone';
import { useNavigate } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface MemberStats {
  userId: string;
  fullName: string;
  email: string;
  todayHours: number;
  isOnline: boolean;
  lastActivity: string | null;
}

interface Screenshot {
  id: string;
  user_id: string | null;
  image_url: string;
  file_path: string | null;
  captured_at: string;
  activity_percent: number | null;
  app_name: string | null;
  active_window_title: string | null;
  classification: string | null;
  category: string | null;
  /* Duplicate fields */
  is_duplicate: boolean | null;
  duplicate_reason: string | null;
  duplicate_group_hash: string | null;
  duplicate_hash: string | null;
  consecutive_duplicate_count: number | null;
  /* AI analysis fields */
  ai_analysis_status: string | null;
  ai_flags: unknown[] | null;
  confidence_score: number | null;
  distraction_score: number | null;
  is_work_related: boolean | null;
  vision_category: string | null;
  vision_content: string | null;
  vision_confidence: number | null;
  keystrokes: number | null;
  mouse_clicks: number | null;
  mouse_movements: number | null;
  focus_percent: number | null;
  url: string | null;
  window_title: string | null;
}

interface AppLog {
  id: string;
  user_id: string;
  app_name: string;
  duration_seconds: number | null;
  category: string | null;
  started_at: string;
}

interface UrlLog {
  id: string | null;
  user_id: string | null;
  url: string | null;
  title: string | null;
  duration_seconds: number | null;
  domain: string | null;
  timestamp: string | null;
}

interface TimeLog {
  id: string;
  user_id: string;
  start_time: string;
  end_time: string | null;
}

interface PeriodReport {
  userId: string;
  fullName: string;
  totalHours: number;
  screenshotCount: number;
  avgActivity: number;
  avgFocus: number;
  workRelatedPct: number;
}

type ReportPeriod = 'daily' | 'weekly' | 'monthly';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function todayStr() {
  return getTodayQatar();
}

/** Get the display URL for a screenshot, preferring image_url then file_path */
function screenshotUrl(ss: Screenshot): string {
  return ss.image_url || ss.file_path || '';
}

/** Get payroll month boundaries (26th → 25th) for a given reference date */
function getPayrollMonth(ref: Date): { start: Date; end: Date; label: string } {
  const y = ref.getFullYear();
  const m = ref.getMonth(); // 0-indexed
  const d = ref.getDate();

  let start: Date;
  let end: Date;

  if (d >= 26) {
    // 26th of current month → 25th of next month
    start = new Date(y, m, 26);
    end = new Date(y, m + 1, 25, 23, 59, 59, 999);
  } else {
    // 26th of previous month → 25th of current month
    start = new Date(y, m - 1, 26);
    end = new Date(y, m, 25, 23, 59, 59, 999);
  }

  const label = `${format(start, 'MMM d')} — ${format(end, 'MMM d, yyyy')}`;
  return { start, end, label };
}

function shiftPayrollMonth(ref: Date, direction: number): Date {
  const y = ref.getFullYear();
  const m = ref.getMonth();
  const d = ref.getDate();

  if (d >= 26) {
    return new Date(y, m + direction, 26);
  } else {
    return new Date(y, m - 1 + direction, 26);
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function TeamLeaderDashboard() {
  const { userDetails } = useAuth();
  const { assignments, loading: assignmentsLoading } = useTeamLeaderAssignments(userDetails?.id);
  const { toast } = useToast();
  const navigate = useNavigate();

  const employeeIds = useMemo(() => assignments.map(a => a.employee_id), [assignments]);
  const nameMap = useMemo(() => {
    const m: Record<string, string> = {};
    assignments.forEach(a => { m[a.employee_id] = a.employee?.full_name || 'Unknown'; });
    return m;
  }, [assignments]);

  /* ---------- Overview state ---------- */
  const [memberStats, setMemberStats] = useState<MemberStats[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);

  /* ---------- Screenshots state ---------- */
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [screenshotDate, setScreenshotDate] = useState(todayStr());
  const [screenshotEmployee, setScreenshotEmployee] = useState('all');
  const [screenshotSearch, setScreenshotSearch] = useState('');
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState<Screenshot | null>(null);

  /* ---------- Activity state ---------- */
  const [appLogs, setAppLogs] = useState<AppLog[]>([]);
  const [urlLogs, setUrlLogs] = useState<UrlLog[]>([]);
  const [activityDate, setActivityDate] = useState(todayStr());
  const [activityEmployee, setActivityEmployee] = useState('all');
  const [activitySearch, setActivitySearch] = useState('');
  const [loadingActivity, setLoadingActivity] = useState(false);

  /* ---------- Time Logs state ---------- */
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [timeLogStart, setTimeLogStart] = useState(todayStr());
  const [timeLogEnd, setTimeLogEnd] = useState(todayStr());
  const [timeLogEmployee, setTimeLogEmployee] = useState('all');
  const [loadingTimeLogs, setLoadingTimeLogs] = useState(false);

  /* ---------- Reports state ---------- */
  const [reports, setReports] = useState<PeriodReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>('weekly');
  const [reportDailyDate, setReportDailyDate] = useState(todayStr());
  const [reportMonthRef, setReportMonthRef] = useState(new Date());

  /* ---------- Active tab ---------- */
  const [activeTab, setActiveTab] = useState('overview');

  /* ================================================================ */
  /*  Helper: get filtered employee IDs based on dropdown             */
  /* ================================================================ */

  const getFilteredIds = (filter: string) => {
    if (filter === 'all') return employeeIds;
    return [filter];
  };

  /* ================================================================ */
  /*  Employee Select Component                                       */
  /* ================================================================ */

  const EmployeeFilter = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[220px]">
        <SelectValue placeholder="All Team Members" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Team Members</SelectItem>
        {assignments.map(a => (
          <SelectItem key={a.employee_id} value={a.employee_id}>
            {a.employee?.full_name || a.employee?.email || 'Unknown'}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  /* ================================================================ */
  /*  Data fetchers                                                   */
  /* ================================================================ */

  const fetchMemberStats = async () => {
    if (employeeIds.length === 0) { setMemberStats([]); return; }
    setLoadingStats(true);
    try {
      const todayRange = getTodayQatarRange();

      const { data: tl, error } = await supabase
        .from('time_logs')
        .select('user_id, start_time, end_time')
        .in('user_id', employeeIds)
        .gte('start_time', todayRange.start)
        .lt('start_time', todayRange.end);
      if (error) throw error;

      const hoursMap: Record<string, number> = {};
      const lastMap: Record<string, string | null> = {};
      const onlineSet = new Set<string>();

      (tl || []).forEach(log => {
        hoursMap[log.user_id] = (hoursMap[log.user_id] || 0) + calculateSessionHours(log.start_time, log.end_time);
        const t = log.end_time || log.start_time;
        if (!lastMap[log.user_id] || t > lastMap[log.user_id]!) lastMap[log.user_id] = t;
        if (!log.end_time) onlineSet.add(log.user_id);
      });

      const stats: MemberStats[] = assignments.map(a => ({
        userId: a.employee_id,
        fullName: a.employee?.full_name || 'Unknown',
        email: a.employee?.email || '',
        todayHours: Math.round((hoursMap[a.employee_id] || 0) * 100) / 100,
        isOnline: onlineSet.has(a.employee_id),
        lastActivity: lastMap[a.employee_id] || null,
      }));
      stats.sort((a, b) => (a.isOnline !== b.isOnline ? (a.isOnline ? -1 : 1) : b.todayHours - a.todayHours));
      setMemberStats(stats);
    } catch (e: any) {
      toast({ title: 'Error loading team stats', description: e.message, variant: 'destructive' });
    } finally {
      setLoadingStats(false);
    }
  };

  const SCREENSHOT_SELECT = [
    'id', 'user_id', 'image_url', 'file_path', 'captured_at', 'activity_percent',
    'app_name', 'active_window_title', 'classification', 'category',
    'is_duplicate', 'duplicate_reason', 'duplicate_group_hash', 'duplicate_hash', 'consecutive_duplicate_count',
    'ai_analysis_status', 'ai_flags', 'confidence_score', 'distraction_score',
    'is_work_related', 'vision_category', 'vision_content', 'vision_confidence',
    'keystrokes', 'mouse_clicks', 'mouse_movements', 'focus_percent', 'url', 'window_title',
  ].join(', ');

  const fetchScreenshots = async () => {
    const ids = getFilteredIds(screenshotEmployee);
    if (ids.length === 0) { setScreenshots([]); return; }
    setLoadingScreenshots(true);
    try {
      const ssRange = getQatarDayRange(screenshotDate);
      const { data, error } = await supabase
        .from('screenshots')
        .select(SCREENSHOT_SELECT)
        .in('user_id', ids)
        .gte('captured_at', ssRange.start)
        .lt('captured_at', ssRange.end)
        .order('captured_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setScreenshots((data as unknown as Screenshot[]) || []);
    } catch (e: any) {
      toast({ title: 'Error loading screenshots', description: e.message, variant: 'destructive' });
    } finally {
      setLoadingScreenshots(false);
    }
  };

  const fetchActivity = async () => {
    const ids = getFilteredIds(activityEmployee);
    if (ids.length === 0) { setAppLogs([]); setUrlLogs([]); return; }
    setLoadingActivity(true);
    try {
      const actRange = getQatarDayRange(activityDate);

      const [appsRes, urlsRes] = await Promise.all([
        supabase
          .from('app_logs')
          .select('id, user_id, app_name, duration_seconds, category, started_at')
          .in('user_id', ids)
          .gte('started_at', actRange.start)
          .lt('started_at', actRange.end)
          .order('duration_seconds', { ascending: false })
          .limit(100),
        supabase
          .from('url_logs')
          .select('id, user_id, url, title, duration_seconds, domain, timestamp')
          .in('user_id', ids)
          .gte('timestamp', actRange.start)
          .lt('timestamp', actRange.end)
          .order('duration_seconds', { ascending: false })
          .limit(100),
      ]);

      if (appsRes.error) throw appsRes.error;
      if (urlsRes.error) throw urlsRes.error;
      setAppLogs(appsRes.data || []);
      setUrlLogs(urlsRes.data || []);
    } catch (e: any) {
      toast({ title: 'Error loading activity', description: e.message, variant: 'destructive' });
    } finally {
      setLoadingActivity(false);
    }
  };

  const fetchTimeLogs = async () => {
    const ids = getFilteredIds(timeLogEmployee);
    if (ids.length === 0) { setTimeLogs([]); return; }
    setLoadingTimeLogs(true);
    try {
      const ds = startOfDayQatar(timeLogStart).toISOString();
      const de = endOfDayQatar(timeLogEnd).toISOString();

      const { data, error } = await supabase
        .from('time_logs')
        .select('id, user_id, start_time, end_time')
        .in('user_id', ids)
        .gte('start_time', ds)
        .lte('start_time', de)
        .order('start_time', { ascending: false })
        .limit(500);
      if (error) throw error;
      setTimeLogs(data || []);
    } catch (e: any) {
      toast({ title: 'Error loading time logs', description: e.message, variant: 'destructive' });
    } finally {
      setLoadingTimeLogs(false);
    }
  };

  const fetchReports = async () => {
    if (employeeIds.length === 0) { setReports([]); return; }
    setLoadingReports(true);
    try {
      let periodStart: string;
      let periodEnd: string;

      if (reportPeriod === 'daily') {
        const dayRange = getQatarDayRange(reportDailyDate);
        periodStart = dayRange.start;
        periodEnd = dayRange.end;
      } else if (reportPeriod === 'weekly') {
        const weekRange = getQatarWeekRange(1); // Monday start
        periodStart = weekRange.start;
        periodEnd = weekRange.end;
      } else {
        // monthly — payroll cycle 26→25
        const pm = getPayrollMonth(reportMonthRef);
        const pmRange = getQatarRangeFromDates(pm.start, pm.end);
        periodStart = pmRange.start;
        periodEnd = pmRange.end;
      }

      const [tlRes, ssRes] = await Promise.all([
        supabase
          .from('time_logs')
          .select('user_id, start_time, end_time')
          .in('user_id', employeeIds)
          .gte('start_time', periodStart)
          .lte('start_time', periodEnd),
        supabase
          .from('screenshots')
          .select('user_id, activity_percent, focus_percent, is_work_related')
          .in('user_id', employeeIds)
          .gte('captured_at', periodStart)
          .lte('captured_at', periodEnd),
      ]);

      if (tlRes.error) throw tlRes.error;
      if (ssRes.error) throw ssRes.error;

      const hoursMap: Record<string, number> = {};
      (tlRes.data || []).forEach(l => {
        hoursMap[l.user_id] = (hoursMap[l.user_id] || 0) + calculateSessionHours(l.start_time, l.end_time);
      });

      const ssCountMap: Record<string, number> = {};
      const actSumMap: Record<string, number> = {};
      const actCountMap: Record<string, number> = {};
      const focusSumMap: Record<string, number> = {};
      const focusCountMap: Record<string, number> = {};
      const workRelatedMap: Record<string, number> = {};
      const workRelatedTotalMap: Record<string, number> = {};

      (ssRes.data || []).forEach(s => {
        const uid = s.user_id;
        if (!uid) return;
        ssCountMap[uid] = (ssCountMap[uid] || 0) + 1;
        if (s.activity_percent != null) {
          actSumMap[uid] = (actSumMap[uid] || 0) + s.activity_percent;
          actCountMap[uid] = (actCountMap[uid] || 0) + 1;
        }
        if (s.focus_percent != null) {
          focusSumMap[uid] = (focusSumMap[uid] || 0) + s.focus_percent;
          focusCountMap[uid] = (focusCountMap[uid] || 0) + 1;
        }
        if (s.is_work_related != null) {
          workRelatedTotalMap[uid] = (workRelatedTotalMap[uid] || 0) + 1;
          if (s.is_work_related) {
            workRelatedMap[uid] = (workRelatedMap[uid] || 0) + 1;
          }
        }
      });

      const reps: PeriodReport[] = assignments.map(a => ({
        userId: a.employee_id,
        fullName: a.employee?.full_name || 'Unknown',
        totalHours: Math.round((hoursMap[a.employee_id] || 0) * 100) / 100,
        screenshotCount: ssCountMap[a.employee_id] || 0,
        avgActivity: actCountMap[a.employee_id]
          ? Math.round((actSumMap[a.employee_id] / actCountMap[a.employee_id]) * 10) / 10
          : 0,
        avgFocus: focusCountMap[a.employee_id]
          ? Math.round((focusSumMap[a.employee_id] / focusCountMap[a.employee_id]) * 10) / 10
          : 0,
        workRelatedPct: workRelatedTotalMap[a.employee_id]
          ? Math.round((workRelatedMap[a.employee_id] || 0) / workRelatedTotalMap[a.employee_id] * 1000) / 10
          : 0,
      }));
      reps.sort((a, b) => b.totalHours - a.totalHours);
      setReports(reps);
    } catch (e: any) {
      toast({ title: 'Error loading reports', description: e.message, variant: 'destructive' });
    } finally {
      setLoadingReports(false);
    }
  };

  /* ================================================================ */
  /*  Effects                                                         */
  /* ================================================================ */

  useEffect(() => {
    if (assignments.length === 0) return;
    if (activeTab === 'overview') fetchMemberStats();
  }, [assignments, activeTab]);

  useEffect(() => {
    if (assignments.length === 0 || activeTab !== 'screenshots') return;
    fetchScreenshots();
    // Also fetch time logs for the selected date so the summary card can show Total Hours
    const fetchTimeLogsForDate = async () => {
      const ids = getFilteredIds(screenshotEmployee);
      if (ids.length === 0) return;
      try {
        const ssTimeRange = getQatarDayRange(screenshotDate);
        const { data } = await supabase
          .from('time_logs')
          .select('id, user_id, start_time, end_time')
          .in('user_id', ids)
          .gte('start_time', ssTimeRange.start)
          .lte('start_time', ssTimeRange.end)
          .limit(200);
        if (data) setTimeLogs(data);
      } catch (_) {}
    };
    fetchTimeLogsForDate();
  }, [assignments, activeTab, screenshotDate, screenshotEmployee]);

  useEffect(() => {
    if (assignments.length === 0 || activeTab !== 'activity') return;
    fetchActivity();
  }, [assignments, activeTab, activityDate, activityEmployee]);

  useEffect(() => {
    if (assignments.length === 0 || activeTab !== 'timelogs') return;
    fetchTimeLogs();
  }, [assignments, activeTab, timeLogStart, timeLogEnd, timeLogEmployee]);

  useEffect(() => {
    if (assignments.length === 0 || activeTab !== 'reports') return;
    fetchReports();
  }, [assignments, activeTab, reportPeriod, reportDailyDate, reportMonthRef]);

  /* ================================================================ */
  /*  Filtered data (client-side search)                              */
  /* ================================================================ */

  const filteredScreenshots = useMemo(() => {
    if (!screenshotSearch.trim()) return screenshots;
    const q = screenshotSearch.toLowerCase();
    return screenshots.filter(ss =>
      (ss.app_name && ss.app_name.toLowerCase().includes(q)) ||
      (ss.active_window_title && ss.active_window_title.toLowerCase().includes(q)) ||
      (ss.user_id && nameMap[ss.user_id]?.toLowerCase().includes(q)) ||
      (ss.category && ss.category.toLowerCase().includes(q))
    );
  }, [screenshots, screenshotSearch, nameMap]);

  const filteredAppLogs = useMemo(() => {
    if (!activitySearch.trim()) return appLogs;
    const q = activitySearch.toLowerCase();
    return appLogs.filter(l =>
      l.app_name.toLowerCase().includes(q) ||
      (l.category && l.category.toLowerCase().includes(q)) ||
      nameMap[l.user_id]?.toLowerCase().includes(q)
    );
  }, [appLogs, activitySearch, nameMap]);

  const filteredUrlLogs = useMemo(() => {
    if (!activitySearch.trim()) return urlLogs;
    const q = activitySearch.toLowerCase();
    return urlLogs.filter(l =>
      (l.url && l.url.toLowerCase().includes(q)) ||
      (l.title && l.title.toLowerCase().includes(q)) ||
      (l.domain && l.domain.toLowerCase().includes(q)) ||
      (l.user_id && nameMap[l.user_id]?.toLowerCase().includes(q))
    );
  }, [urlLogs, activitySearch, nameMap]);

  /* ================================================================ */
  /*  Screenshot navigation                                           */
  /* ================================================================ */

  const navigateScreenshot = (direction: 'prev' | 'next') => {
    if (!selectedScreenshot) return;
    const idx = filteredScreenshots.findIndex(s => s.id === selectedScreenshot.id);
    if (idx === -1) return;
    const newIdx = direction === 'prev' ? idx - 1 : idx + 1;
    if (newIdx >= 0 && newIdx < filteredScreenshots.length) {
      setSelectedScreenshot(filteredScreenshots[newIdx]);
    }
  };

  /* ================================================================ */
  /*  Derived values                                                  */
  /* ================================================================ */

  const totalHoursToday = memberStats.reduce((s, m) => s + m.todayHours, 0);
  const onlineCount = memberStats.filter(m => m.isOnline).length;
  const loading = assignmentsLoading || loadingStats;

  /* ================================================================ */
  /*  Report period helpers                                           */
  /* ================================================================ */

  const reportPeriodLabel = useMemo(() => {
    if (reportPeriod === 'daily') return `Daily — ${format(new Date(reportDailyDate), 'MMM d, yyyy')}`;
    if (reportPeriod === 'weekly') {
      const wRange = getQatarWeekRange(1); // Monday start
      return `Weekly — ${format(new Date(wRange.start), 'MMM d')} – ${format(new Date(wRange.end), 'MMM d, yyyy')}`;
    }
    return `Monthly — ${getPayrollMonth(reportMonthRef).label}`;
  }, [reportPeriod, reportDailyDate, reportMonthRef]);

  const reportSummary = useMemo(() => {
    if (reports.length === 0) return null;
    const totalHours = reports.reduce((s, r) => s + r.totalHours, 0);
    const totalSS = reports.reduce((s, r) => s + r.screenshotCount, 0);
    const avgAct = reports.length > 0 ? Math.round(reports.reduce((s, r) => s + r.avgActivity, 0) / reports.length * 10) / 10 : 0;
    const avgFoc = reports.length > 0 ? Math.round(reports.reduce((s, r) => s + r.avgFocus, 0) / reports.length * 10) / 10 : 0;
    const avgWR = reports.length > 0 ? Math.round(reports.reduce((s, r) => s + r.workRelatedPct, 0) / reports.length * 10) / 10 : 0;
    return { totalHours, totalSS, avgAct, avgFoc, avgWR };
  }, [reports]);

  /* ================================================================ */
  /*  Render helpers                                                  */
  /* ================================================================ */

  const Spinner = () => (
    <div className="flex justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  const Empty = ({ message }: { message: string }) => (
    <p className="text-center text-muted-foreground py-12">{message}</p>
  );

  const activityBadge = (pct: number | null) => {
    if (pct == null) return <span className="text-muted-foreground">N/A</span>;
    const variant = pct >= 70 ? 'default' : pct >= 40 ? 'outline' : 'destructive';
    return <Badge variant={variant}>{pct}%</Badge>;
  };

  /* ================================================================ */
  /*  Tab: Overview                                                   */
  /* ================================================================ */

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignments.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Online Now</CardTitle>
            <Activity className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{onlineCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Hours Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalHoursToday.toFixed(1)}h</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Spinner /> : memberStats.length === 0 ? (
            <Empty message="No team members assigned yet. Contact your admin to assign employees to your team." />
          ) : (
            <div className="space-y-3">
              {memberStats.map(member => (
                <div
                  key={member.userId}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${member.isOnline ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <div>
                      <p className="font-medium">{member.fullName}</p>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{member.todayHours.toFixed(1)}h today</p>
                      {member.lastActivity && (
                        <p className="text-xs text-muted-foreground">
                          Last: {format(new Date(member.lastActivity), 'HH:mm')}
                        </p>
                      )}
                    </div>
                    <Badge variant={member.isOnline ? 'default' : 'secondary'}>
                      {member.isOnline ? 'Online' : 'Offline'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/team-leader/employee/${member.userId}`)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  /* ================================================================ */
  /*  Tab: Screenshots                                                */
  /* ================================================================ */

  const screenshotSummary = useMemo(() => {
    const ss = screenshotEmployee === 'all' ? screenshots : screenshots.filter(s => s.user_id === screenshotEmployee);
    const total = ss.length;
    const actValues = ss.filter(s => s.activity_percent != null).map(s => s.activity_percent as number);
    const avgActivity = actValues.length > 0 ? Math.round(actValues.reduce((a, b) => a + b, 0) / actValues.length) : null;
    // Idle: screenshots with activity < 10%
    const idleCount = ss.filter(s => s.activity_percent != null && (s.activity_percent as number) < 10).length;
    const idleMinutes = idleCount * 10; // each screenshot ~ 10 min interval
    return { total, avgActivity, idleMinutes };
  }, [screenshots, screenshotEmployee]);

  const screenshotDayHours = useMemo(() => {
    // Sum time_logs for the selected date + employee
    const ids = getFilteredIds(screenshotEmployee);
    const dayStr = screenshotDate;
    const relevant = timeLogs.filter(l => {
      if (!ids.includes(l.user_id)) return false;
      const dateStr = l.start_time.slice(0, 10);
      return dateStr === dayStr;
    });
    return relevant.reduce((s, l) => s + calculateSessionHours(l.start_time, l.end_time), 0);
  }, [timeLogs, screenshotDate, screenshotEmployee]);

  const shiftScreenshotDate = (days: number) => {
    const d = new Date(screenshotDate);
    d.setDate(d.getDate() + days);
    setScreenshotDate(d.toISOString().slice(0, 10));
  };

  const renderScreenshots = () => (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Date:</label>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftScreenshotDate(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <input
                type="date"
                value={screenshotDate}
                onChange={e => setScreenshotDate(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm bg-background"
              />
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftScreenshotDate(1)} disabled={screenshotDate >= todayStr()}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Employee:</label>
              <EmployeeFilter value={screenshotEmployee} onChange={setScreenshotEmployee} />
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by app, title, employee..."
                  value={screenshotSearch}
                  onChange={e => setScreenshotSearch(e.target.value)}
                  className="pl-9"
                />
                {screenshotSearch && (
                  <button
                    onClick={() => setScreenshotSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
            <Badge variant="outline" className="whitespace-nowrap">
              {filteredScreenshots.length} screenshot{filteredScreenshots.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {!loadingScreenshots && screenshots.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Total Hours</p>
                <p className="text-lg font-bold">{screenshotDayHours > 0 ? `${screenshotDayHours.toFixed(1)}h` : '—'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Activity className="h-5 w-5 text-green-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Avg Activity</p>
                <p className="text-lg font-bold">{screenshotSummary.avgActivity != null ? `${screenshotSummary.avgActivity}%` : '—'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-orange-400 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Idle Time</p>
                <p className="text-lg font-bold">{screenshotSummary.idleMinutes > 0 ? `${screenshotSummary.idleMinutes}m` : '0m'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Camera className="h-5 w-5 text-purple-500 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Total Screenshots</p>
                <p className="text-lg font-bold">{screenshotSummary.total}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loadingScreenshots ? <Spinner /> : filteredScreenshots.length === 0 ? (
        <Empty message="No screenshots found for the selected filters." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredScreenshots.map(ss => {
            const imgSrc = screenshotUrl(ss);
            return (
              <Card
                key={ss.id}
                className="overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                onClick={() => setSelectedScreenshot(ss)}
              >
                <div className="relative aspect-video bg-muted">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt="Screenshot"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
                      No image
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white px-2 py-1 text-xs flex justify-between">
                    <span>{(ss.user_id && nameMap[ss.user_id]) || 'Unknown'}</span>
                    <span>{format(new Date(ss.captured_at), 'HH:mm')}</span>
                  </div>
                  {ss.is_duplicate && (
                    <div className="absolute top-2 right-2">
                      <Badge variant="destructive" className="text-xs">Duplicate</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-3 space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    {ss.app_name && <span className="truncate">{ss.app_name}</span>}
                    {ss.activity_percent != null && (
                      <Badge variant={ss.activity_percent >= 50 ? 'default' : 'destructive'} className="text-xs ml-1">
                        {ss.activity_percent}%
                      </Badge>
                    )}
                  </div>
                  {ss.active_window_title && (
                    <p className="text-xs text-muted-foreground truncate" title={ss.active_window_title}>
                      {ss.active_window_title}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Screenshot Detail Modal */}
      <Dialog open={!!selectedScreenshot} onOpenChange={() => setSelectedScreenshot(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedScreenshot && (() => {
            const ss = selectedScreenshot;
            const imgSrc = screenshotUrl(ss);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span>
                      {(ss.user_id && nameMap[ss.user_id]) || 'Unknown'} —{' '}
                      {format(new Date(ss.captured_at), 'MMM d, yyyy HH:mm:ss')}
                    </span>
                    {ss.is_duplicate && <Badge variant="destructive">Duplicate</Badge>}
                  </DialogTitle>
                </DialogHeader>

                {/* Full-size screenshot */}
                <div className="relative">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt="Full screenshot"
                      className="w-full h-auto rounded-md border"
                      onError={e => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full aspect-video bg-muted rounded-md flex items-center justify-center">
                      <Camera className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}

                  {/* Prev / Next buttons */}
                  <div className="absolute inset-y-0 left-0 flex items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 bg-black/30 hover:bg-black/50 text-white rounded-full ml-2"
                      onClick={(e) => { e.stopPropagation(); navigateScreenshot('prev'); }}
                      disabled={filteredScreenshots.findIndex(s => s.id === ss.id) === 0}
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 bg-black/30 hover:bg-black/50 text-white rounded-full mr-2"
                      onClick={(e) => { e.stopPropagation(); navigateScreenshot('next'); }}
                      disabled={filteredScreenshots.findIndex(s => s.id === ss.id) === filteredScreenshots.length - 1}
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </div>
                </div>

                {/* Duplicate info */}
                {ss.is_duplicate && (
                  <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md p-3 space-y-1">
                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Duplicate Screenshot</p>
                    {ss.duplicate_reason && (
                      <p className="text-xs text-red-600 dark:text-red-400">Reason: {ss.duplicate_reason}</p>
                    )}
                    {(ss.consecutive_duplicate_count ?? 0) > 0 && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Consecutive duplicates: {ss.consecutive_duplicate_count}
                      </p>
                    )}
                  </div>
                )}

                {/* Details grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Employee</p>
                    <p className="text-sm font-medium">
                      {(ss.user_id && nameMap[ss.user_id]) || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Activity %</p>
                    <div className="text-sm font-medium">{activityBadge(ss.activity_percent)}</div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Focus %</p>
                    <div className="text-sm font-medium">
                      {ss.focus_percent != null ? activityBadge(ss.focus_percent) : <span className="text-muted-foreground">N/A</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">AI Analysis</p>
                    <p className="text-sm font-medium">
                      {ss.ai_analysis_status ? (
                        <Badge variant={ss.ai_analysis_status === 'completed' ? 'default' : ss.ai_analysis_status === 'failed' ? 'destructive' : 'outline'}>
                          {ss.ai_analysis_status}
                        </Badge>
                      ) : <span className="text-muted-foreground">N/A</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Application</p>
                    <p className="text-sm font-medium truncate">{ss.app_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Classification</p>
                    <p className="text-sm font-medium">{ss.classification || ss.category || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Work Related</p>
                    <p className="text-sm font-medium">
                      {ss.is_work_related != null ? (
                        <Badge variant={ss.is_work_related ? 'default' : 'destructive'}>
                          {ss.is_work_related ? 'Yes' : 'No'}
                        </Badge>
                      ) : <span className="text-muted-foreground">N/A</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Distraction Score</p>
                    <p className="text-sm font-medium">
                      {ss.distraction_score != null ? `${ss.distraction_score}%` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Keystrokes</p>
                    <p className="text-sm font-medium">{ss.keystrokes ?? 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Mouse Clicks</p>
                    <p className="text-sm font-medium">{ss.mouse_clicks ?? 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Confidence Score</p>
                    <p className="text-sm font-medium">
                      {ss.confidence_score != null ? `${ss.confidence_score}%` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Vision Category</p>
                    <p className="text-sm font-medium">{ss.vision_category || 'N/A'}</p>
                  </div>
                </div>

                {/* Vision content */}
                {ss.vision_content && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">Vision Content</p>
                    <p className="text-sm" title={ss.vision_content}>
                      {ss.vision_content.length > 100 ? ss.vision_content.slice(0, 100) + '...' : ss.vision_content}
                    </p>
                  </div>
                )}

                {/* URL */}
                {ss.url && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">URL</p>
                    <a
                      href={ss.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline truncate block"
                      title={ss.url}
                    >
                      {ss.url.length > 80 ? ss.url.slice(0, 80) + '...' : ss.url}
                    </a>
                  </div>
                )}

                {/* Window title */}
                {(ss.window_title || ss.active_window_title) && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">Window Title</p>
                    <p className="text-sm">{ss.window_title || ss.active_window_title}</p>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );

  /* ================================================================ */
  /*  Tab: Activity                                                   */
  /* ================================================================ */

  const renderActivity = () => (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Date:</label>
              <input
                type="date"
                value={activityDate}
                onChange={e => setActivityDate(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Employee:</label>
              <EmployeeFilter value={activityEmployee} onChange={setActivityEmployee} />
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search apps or URLs..."
                  value={activitySearch}
                  onChange={e => setActivitySearch(e.target.value)}
                  className="pl-9"
                />
                {activitySearch && (
                  <button
                    onClick={() => setActivitySearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loadingActivity ? <Spinner /> : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top Apps */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Monitor className="h-4 w-4" /> Applications ({filteredAppLogs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredAppLogs.length === 0 ? (
                <Empty message="No app usage data found." />
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredAppLogs.map(log => (
                    <div key={log.id} className="flex items-center justify-between p-3 rounded-lg border text-sm hover:bg-muted/30">
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-medium truncate">{log.app_name}</span>
                        <span className="text-xs text-muted-foreground">{nameMap[log.user_id] || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <span className="text-sm font-medium">{formatDuration(log.duration_seconds || 0)}</span>
                        {log.category && (
                          <Badge variant="outline" className="text-xs">{log.category}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top URLs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4" /> URLs Visited ({filteredUrlLogs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredUrlLogs.length === 0 ? (
                <Empty message="No URL data found." />
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredUrlLogs.map((log, idx) => (
                    <div key={log.id || idx} className="flex items-center justify-between p-3 rounded-lg border text-sm hover:bg-muted/30">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{log.title || log.domain || log.url || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {(log.user_id && nameMap[log.user_id]) || 'Unknown'}
                          {log.domain && ` · ${log.domain}`}
                        </p>
                      </div>
                      <span className="text-sm font-medium shrink-0 ml-3">{formatDuration(log.duration_seconds || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Tab: Time Logs                                                  */
  /* ================================================================ */

  const renderTimeLogs = () => (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">From:</label>
              <input
                type="date"
                value={timeLogStart}
                onChange={e => setTimeLogStart(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">To:</label>
              <input
                type="date"
                value={timeLogEnd}
                onChange={e => setTimeLogEnd(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Employee:</label>
              <EmployeeFilter value={timeLogEmployee} onChange={setTimeLogEmployee} />
            </div>
            <Badge variant="outline" className="whitespace-nowrap">
              {timeLogs.length} session{timeLogs.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {loadingTimeLogs ? <Spinner /> : timeLogs.length === 0 ? (
        <Empty message="No time logs found for the selected filters." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Employee</th>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-left p-3 font-medium">Start</th>
                    <th className="text-left p-3 font-medium">End</th>
                    <th className="text-left p-3 font-medium">Duration</th>
                    <th className="text-left p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {timeLogs.map(log => {
                    const hours = calculateSessionHours(log.start_time, log.end_time);
                    return (
                      <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="p-3 font-medium">{nameMap[log.user_id] || 'Unknown'}</td>
                        <td className="p-3">{format(new Date(log.start_time), 'MMM d, yyyy')}</td>
                        <td className="p-3">{format(new Date(log.start_time), 'HH:mm')}</td>
                        <td className="p-3">{log.end_time ? format(new Date(log.end_time), 'HH:mm') : '—'}</td>
                        <td className="p-3">{hours.toFixed(2)}h</td>
                        <td className="p-3">
                          <Badge variant={log.end_time ? 'secondary' : 'default'}>
                            {log.end_time ? 'Completed' : 'Active'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Tab: Reports                                                    */
  /* ================================================================ */

  const renderReports = () => (
    <div className="space-y-4">
      {/* Period selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Period:</label>
              <Select value={reportPeriod} onValueChange={v => setReportPeriod(v as ReportPeriod)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reportPeriod === 'daily' && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">Date:</label>
                <input
                  type="date"
                  value={reportDailyDate}
                  onChange={e => setReportDailyDate(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm bg-background"
                />
              </div>
            )}

            {reportPeriod === 'monthly' && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setReportMonthRef(prev => shiftPayrollMonth(prev, -1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">{getPayrollMonth(reportMonthRef).label}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setReportMonthRef(prev => shiftPayrollMonth(prev, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            <p className="text-sm text-muted-foreground ml-auto">{reportPeriodLabel}</p>
          </div>
        </CardContent>
      </Card>

      {loadingReports ? <Spinner /> : reports.length === 0 ? (
        <Empty message="No report data available." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Employee</th>
                    <th className="text-left p-3 font-medium">Total Hours</th>
                    <th className="text-left p-3 font-medium">Screenshots</th>
                    <th className="text-left p-3 font-medium">Avg Activity %</th>
                    <th className="text-left p-3 font-medium">Avg Focus %</th>
                    <th className="text-left p-3 font-medium">Work Related %</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => (
                    <tr key={r.userId} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="p-3 font-medium">{r.fullName}</td>
                      <td className="p-3">{r.totalHours.toFixed(1)}h</td>
                      <td className="p-3">{r.screenshotCount}</td>
                      <td className="p-3">{activityBadge(r.avgActivity)}</td>
                      <td className="p-3">{activityBadge(r.avgFocus)}</td>
                      <td className="p-3">{r.workRelatedPct}%</td>
                    </tr>
                  ))}
                  {/* Summary row */}
                  {reportSummary && (
                    <tr className="border-t-2 bg-muted/50 font-semibold">
                      <td className="p-3">Total / Average</td>
                      <td className="p-3">{reportSummary.totalHours.toFixed(1)}h</td>
                      <td className="p-3">{reportSummary.totalSS}</td>
                      <td className="p-3">{activityBadge(reportSummary.avgAct)}</td>
                      <td className="p-3">{activityBadge(reportSummary.avgFoc)}</td>
                      <td className="p-3">{reportSummary.avgWR}%</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Main render                                                     */
  /* ================================================================ */

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team Dashboard"
        subtitle={`Managing ${assignments.length} team member${assignments.length !== 1 ? 's' : ''}`}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" className="gap-1.5">
            <Users className="h-3.5 w-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="screenshots" className="gap-1.5">
            <Camera className="h-3.5 w-3.5" /> Screenshots
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-1.5">
            <Activity className="h-3.5 w-3.5" /> Activity
          </TabsTrigger>
          <TabsTrigger value="timelogs" className="gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Time Logs
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" /> Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">{renderOverview()}</TabsContent>
        <TabsContent value="screenshots" className="mt-6">{renderScreenshots()}</TabsContent>
        <TabsContent value="activity" className="mt-6">{renderActivity()}</TabsContent>
        <TabsContent value="timelogs" className="mt-6">{renderTimeLogs()}</TabsContent>
        <TabsContent value="reports" className="mt-6">{renderReports()}</TabsContent>
      </Tabs>
    </div>
  );
}
